import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { 
  StarIcon, 
  ClockIcon, 
  AcademicCapIcon, 
  UserIcon, 
  CalendarIcon, 
  CheckCircleIcon,
  ChevronRightIcon,
  GlobeAltIcon,
  ShareIcon,
  HeartIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon, HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid'
import { SkeletonCard, SkeletonText } from '../../components/ui/Loading'

// ... (保留原有的 Course 接口)
interface Course {
  id: string
  title: string
  description: string
  subject: string
  expectedPricePerHour?: string
  finalPricePerHour?: string
  price: string
  duration: string
  status: string
  coverImage?: string
  createdAt: string
  teacher?: {
    id: string
    title?: string
    introduction?: string
    specialties?: string[]
    rating?: string
    bio?: string
    experience?: string
    education?: string
    user?: {
      firstName?: string
      lastName?: string
      avatar?: string
    }
  }
  _count?: {
    bookings: number
  }
}

const CourseDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [course, setCourse] = useState<Course | null>(null)
  const [loading, setLoading] = useState(true)
  const [bookingLoading, setBookingLoading] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isFavorite, setIsFavorite] = useState(false)
  const [activeTab, setActiveTab] = useState('overview') // overview, curriculum, reviews

  useEffect(() => {
    if (id) {
      fetchCourse(id)
    }
    checkAuth()
  }, [id])

  const checkAuth = () => {
    const token = localStorage.getItem('token')
    setIsAuthenticated(!!token)
  }

  const fetchCourse = async (courseId: string) => {
    try {
      const response = await fetch(`/api/courses/${courseId}`)
      if (response.ok) {
        const data = await response.json()
        // 处理不同的响应格式
        const courseData = data.data?.course || data.course || data
        console.log('获取到的课程数据:', courseData)
        setCourse(courseData)
      } else {
        console.error('获取课程详情失败')
      }
    } catch (error) {
      console.error('获取课程详情失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleBooking = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/courses/${id}` } })
      return
    }
    navigate(`/booking/${id}`)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="aspect-video bg-gray-200 rounded-2xl animate-pulse" />
              <SkeletonText lines={4} />
            </div>
            <div className="lg:col-span-1">
              <SkeletonCard />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">课程不存在</h1>
          <Link to="/courses" className="btn-primary-new">
            返回课程列表
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 沉浸式头部 Hero */}
      <div className="relative h-[500px] lg:h-[600px] bg-gray-900 overflow-hidden">
        {/* 背景图 - 带模糊和遮罩 */}
        <div className="absolute inset-0 z-0">
          <img 
            src={course.coverImage || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=800&fit=crop"} 
            alt={course.title}
            className="w-full h-full object-cover opacity-50 blur-sm scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent" />
        </div>

        {/* 内容 */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-24 lg:pb-32">
          <div className="max-w-3xl animate-slide-up">
            <div className="flex items-center space-x-3 mb-4 text-sm font-medium">
              <span className="px-3 py-1 rounded-full bg-blue-600 text-white">
                {course.subject}
              </span>
              <span className="flex items-center text-yellow-400">
                <StarSolidIcon className="w-4 h-4 mr-1" />
                {(parseFloat(course.teacher?.rating || '0') || 0).toFixed(1)} 分
              </span>
              <span className="text-gray-300">
                • {course._count?.bookings || 0} 人已学
              </span>
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {course.title}
            </h1>
            
            <div className="flex items-center space-x-6 text-gray-200">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-gray-700 overflow-hidden mr-3 border-2 border-white/20">
                   {course.teacher?.user?.avatar ? (
                      <img src={course.teacher.user.avatar} className="w-full h-full object-cover" alt="avatar" />
                   ) : (
                      <div className="w-full h-full flex items-center justify-center text-white font-bold">
                        {course.teacher?.user?.firstName?.[0] || '师'}
                      </div>
                   )}
                </div>
                <div>
                  <p className="text-sm text-gray-400">授课教师</p>
                  <p className="font-medium text-white">{course.teacher?.user?.firstName || ''} {course.teacher?.user?.lastName || '老师'}</p>
                </div>
              </div>
              <div className="hidden sm:block h-10 w-px bg-white/20" />
              <div className="hidden sm:flex items-center">
                <ClockIcon className="w-5 h-5 mr-2 text-blue-400" />
                <span>{course.duration} 课时</span>
              </div>
              <div className="hidden sm:flex items-center">
                <GlobeAltIcon className="w-5 h-5 mr-2 text-green-400" />
                <span>在线授课</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-20 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 左侧详情 */}
          <div className="lg:col-span-2 space-y-8">
            {/* 选项卡导航 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 flex space-x-2 sticky top-24 z-30">
              {['overview', 'curriculum', 'reviews'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    activeTab === tab
                      ? 'bg-blue-50 text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  }`}
                >
                  {tab === 'overview' ? '课程概览' : tab === 'curriculum' ? '课程大纲' : '学员评价'}
                </button>
              ))}
            </div>

            {/* 概览内容 */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-8 animate-fade-in">
              <section>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <BookOpenIcon className="w-5 h-5 text-blue-600 mr-2" />
                  课程介绍
                </h3>
                <div className="prose max-w-none text-gray-600 leading-relaxed">
                  {course.description}
                </div>
              </section>

              <section>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                  你将学到什么
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['掌握核心知识点', '提升解题技巧', '个性化学习方案', '实时互动答疑'].map((item, i) => (
                    <div key={i} className="flex items-start">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="pt-6 border-t border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6">关于讲师</h3>
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 rounded-2xl bg-gray-100 overflow-hidden shadow-md">
                      {course.teacher?.user?.avatar ? (
                        <img src={course.teacher.user.avatar} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl text-gray-400 bg-gray-200">
                          <UserIcon className="w-12 h-12" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 mb-1">
                      {course.teacher?.user?.firstName || ''} {course.teacher?.user?.lastName || '老师'}
                    </h4>
                    <p className="text-blue-600 font-medium text-sm mb-3">{course.teacher?.title || '教师'}</p>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {course.teacher?.bio || course.teacher?.introduction || '暂无简介'}
                    </p>
                    
                    <div className="flex flex-wrap gap-2">
                      {(course.teacher?.specialties || []).map((tag, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* 右侧粘性侧边栏 */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 overflow-hidden relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500" />
                
                <div className="mb-6">
                  <span className="text-sm text-gray-500 font-medium block mb-1">课程价格</span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-gray-900">
                      ¥{course.finalPricePerHour || course.price}
                    </span>
                    <span className="text-gray-500 font-medium">/ 课时</span>
                  </div>
                  {parseFloat(course.price) < parseFloat(course.finalPricePerHour || '0') && (
                    <div className="mt-2 inline-flex items-center px-2 py-1 rounded bg-red-50 text-red-600 text-xs font-bold">
                      限时优惠 {(parseFloat(course.price) / parseFloat(course.finalPricePerHour!) * 10).toFixed(1)} 折
                    </div>
                  )}
                </div>

                <button
                  onClick={handleBooking}
                  disabled={bookingLoading}
                  className="w-full btn-primary-new text-lg mb-4 py-4"
                >
                  {bookingLoading ? '处理中...' : '立即预约试听'}
                  <ChevronRightIcon className="w-5 h-5 ml-1" />
                </button>

                <button
                  onClick={() => setIsFavorite(!isFavorite)}
                  className={`w-full flex items-center justify-center px-4 py-3 rounded-xl border font-medium transition-all ${
                    isFavorite 
                      ? 'border-red-200 bg-red-50 text-red-600' 
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {isFavorite ? (
                    <HeartSolidIcon className="w-5 h-5 mr-2" />
                  ) : (
                    <HeartIcon className="w-5 h-5 mr-2" />
                  )}
                  {isFavorite ? '已收藏' : '收藏课程'}
                </button>

                <div className="mt-6 space-y-4 pt-6 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center mr-3 text-blue-600">
                      <GlobeAltIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">随时随地学习</p>
                      <p className="text-xs">支持手机、平板和电脑</p>
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-3 text-green-600">
                      <CheckCircleIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">满意度保障</p>
                      <p className="text-xs">首课不满意全额退款</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 咨询卡片 */}
              <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-2xl p-6 text-white shadow-lg">
                <h4 className="text-lg font-bold mb-2">有疑问？</h4>
                <p className="text-blue-100 text-sm mb-4">
                  我们的课程顾问随时为您解答，帮助您制定学习计划。
                </p>
                <button className="w-full py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg text-sm font-medium transition-colors border border-white/30">
                  联系客服
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// 补充缺失的图标组件
function BookOpenIcon(props: any) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  )
}

export default CourseDetail